import { Icons } from '@/components/icons';
import { Button } from '@/components/primitives/Button';
import {
  PopoverContent,
  PopoverItem,
  PopoverRoot,
  PopoverTrigger,
} from '@/components/primitives/Popover';
import { Typography } from '@/components/primitives/Typography';
import { tenantSelector } from '@/features/configuration/configurationSelector';
import {
  type I18nLanguage,
  localeConfigurations,
  useTranslation,
} from '@/i18n';
import { useSelector } from '@/store/utils';
import { typedEntries } from '@/utils/object';
import { getTenantConfiguration } from '@/utils/tenant';
import { useTranslation as useWidgetTranslation } from '@packages/webshop-search-widget';
import type { FC } from 'react';

interface RegularLanguageSelectProps {}

const RegularLanguageSelect: FC<RegularLanguageSelectProps> = () => {
  const {
    i18n: { changeLanguage, language: currentLanguage },
  } = useTranslation();
  const {
    i18n: { changeLanguage: changeWidgetLanguage },
  } = useWidgetTranslation();
  const currentTenant = useSelector(tenantSelector);
  const tenantConfiguration = getTenantConfiguration(currentTenant.value);

  return (
    <PopoverRoot>
      <PopoverTrigger asChild>
        <Button
          data-testid="language-select-trigger"
          variant="ghostLink"
          className="group flex gap-2"
        >
          <Icons.globe height={24} width={24} />
          {localeConfigurations[currentLanguage as I18nLanguage].label}
          <Icons.chevronDown
            height={16}
            width={16}
            className="transition-transform duration-200 group-data-[state=open]:rotate-180"
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-40 overflow-hidden rounded-md p-0"
        align="end"
      >
        {typedEntries(localeConfigurations)
          .filter(([code]) =>
            tenantConfiguration?.languageOptions.includes(code)
          )
          .map(([code, config], key) => (
            <PopoverItem
              key={key}
              active={code === currentLanguage}
              onClick={() => {
                changeLanguage(code);
                changeWidgetLanguage(code);
              }}
              data-testid={`language-select-choose-${code}`}
            >
              <Typography variant="body1">{config.label}</Typography>
              {code === currentLanguage && (
                <Icons.checkCircle
                  height={16}
                  width={16}
                  className="text-success-text"
                />
              )}
            </PopoverItem>
          ))}
      </PopoverContent>
    </PopoverRoot>
  );
};

export default RegularLanguageSelect;
